<template>
  <div class="column full-height">
    <header
      class="row items-center justify-between q-px-2x-md full-width no-wrap"
      :style="{ height: '86px' }"
    >
      <span
        class="text-lg text-neutral text-weight-medium"
        :style="{ maxWidth: '75%' }"
      >
        Associated {{ voucherDetail?.label }}
      </span>

      <PBtn color="primary" flat round @click="onClose">
        <CloseIcon class="icon" />
      </PBtn>
    </header>

    <q-separator />

    <div class="q-px-2x-md q-py-xl col overflow-auto">
      <div v-if="hasRecords">
        <div
          v-for="(record, index) in voucherDetail.records"
          :key="index"
          class="q-mb-xl"
        >
          <div class="flex justify-between">
            <div v-if="computedUnclickableLabels">
              <span>{{ record.number || record.internal_number }}</span>
            </div>
            <PBtn
              v-else
              flat
              dense
              class="q-pa-none"
              target="_blank"
              :to="resolveVoucherRoute(record)"
            >
              {{ record.number || record.internal_number }}
            </PBtn>
            <PBadge
              v-if="showStatusBadge"
              :label="record?.status"
              :type="record?.status"
            />
          </div>

          <q-separator class="q-my-xs" />

          <div class="row q-mt-xs text-neutral text-sm">
            <div class="col-4 text-neutral-80">Date</div>
            <div class="col-8">
              {{
                formatDate(
                  record?.date || record?.processed_payment_date,
                  "DD MMM YYYY",
                )
              }}
            </div>
          </div>

          <div
            v-if="showStage(record)"
            class="row q-mt-xs text-neutral text-sm"
          >
            <div class="col-4 text-neutral-80">Stage</div>
            <div class="col-8">
              <PBadge :label="record?.stage" :type="record?.stage" />
            </div>
          </div>

          <div v-if="showUTR" class="row q-mt-xs text-neutral text-sm">
            <div class="col-4 text-neutral-80">UTR</div>
            <div class="col-8">
              {{ record?.reference_number }}
            </div>
          </div>

          <div v-if="showAmount" class="row q-mt-xs text-neutral text-sm">
            <div class="col-4 text-neutral-80">Amount</div>
            <div class="col-8">
              {{
                numberFormat({
                  number: getComputedAmount(record),
                  style: "currency",
                  mfd: 2,
                })
              }}
            </div>
          </div>

          <div
            v-if="showVehicleNumber"
            class="row q-mt-xs text-neutral text-sm"
          >
            <div class="col-4 text-neutral-80">Vehicle No.</div>
            <div class="col-8">
              {{ record?.vehicle_number }}
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-neutral">
        No Associated {{ voucherDetail?.label }} Found
      </div>
    </div>
  </div>
</template>

<script setup>
import { date } from "quasar";
import CloseIcon from "assets/icons/close.svg";
import { numberFormat } from "assets/scripts/utilities/helpers";
import { generateRoute } from "src/assets/scripts/utilities/routeGenerator";

const { formatDate } = date;

const props = defineProps({
  section: {
    type: String,
    default: "",
  },
  voucherDetail: {
    type: Object,
    default: () => ({}),
  },
  onClose: {
    type: Function,
    default: () => {},
  },
});

const hasRecords = computed(() => !!props.voucherDetail?.count);

const showStatusBadge = computed(
  () => props.voucherDetail?.label !== "Payments",
);

const unclickableLabels = new Set(["Quality Checks"]);
const unclickableSections = new Set(["customer_receivables"]);

const computedUnclickableLabels = computed(() => {
  return (
    unclickableLabels.has(props.voucherDetail?.label) ||
    unclickableSections.has(props.section)
  );
});

const showStage = () =>
  ["E-Way Bill", "Invoices", "Debit Notes", "Credit Notes"].includes(
    props.voucherDetail?.label,
  );

const showUTR = computed(() => props.voucherDetail?.label === "Payments");

const showAmount = computed(() =>
  [
    "Purchase Orders",
    "Invoices",
    "Debit Notes",
    "Credit Notes",
    "Payments",
    "Purchase Bills",
    "Proof of Deliveries",
    "Quality Checks",
  ].includes(props.voucherDetail?.label),
);

const getComputedAmount = (record) => {
  return (
    record?.grand_total || record?.net_payable || record?.total_amount || 0
  );
};

const showVehicleNumber = computed(
  () => props.voucherDetail?.label === "E-Way Bill",
);

const resolveVoucherRoute = (record) => {
  const data = generateRoute(props.voucherDetail?.label, record, {
    section: props.section,
    openDrawer: true,
    entityId: record._id,
  });

  return data;
};
</script>
